/**
 * Complete reselect shim for Webpack 5.
 *
 * Webpack 5 treats .cjs files as static assets (asset/resource), which means
 * reselect (which ships as .cjs) gets emitted as a separate file in /static/media/
 * instead of being bundled as JS code. This causes createSelector to be undefined
 * at runtime, crashing recharts and @reduxjs/toolkit.
 *
 * This shim provides the full reselect API that the app needs:
 * - createSelector (used by recharts, @reduxjs/toolkit)
 * - createSelectorCreator (used by @reduxjs/toolkit)
 * - createStructuredSelector (used by connected components)
 * - defaultEqualityCheck, setGlobalDevModeChecks
 *
 * The webpack resolve.alias replaces all imports of 'reselect' with this file.
 */

export function defaultEqualityCheck(a, b) {
  return a === b;
}

export function defaultMemoize(func, equalityCheck = defaultEqualityCheck) {
  let lastArgs = null;
  let lastResult = null;
  return (...args) => {
    if (
      lastArgs !== null &&
      args.length === lastArgs.length &&
      args.every((arg, i) => equalityCheck(arg, lastArgs[i]))
    ) {
      return lastResult;
    }
    lastArgs = args;
    lastResult = func(...args);
    return lastResult;
  };
}

export function createSelectorCreator(memoize, ...memoizeOptions) {
  return function createSelector(...args) {
    const resultFunc = args.pop();
    const dependencies =
      args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
    const memoizedResultFunc = memoize(resultFunc, ...memoizeOptions);
    return (state, ...rest) => {
      const params = dependencies.map((dep) => dep(state, ...rest));
      return memoizedResultFunc(...params);
    };
  };
}

export const createSelector = createSelectorCreator(defaultMemoize);

export function createStructuredSelector(selectors) {
  return (state, ...rest) => {
    const result = {};
    for (const key of Object.keys(selectors)) {
      result[key] = selectors[key](state, ...rest);
    }
    return result;
  };
}

export const setGlobalDevModeChecks = () => {};
